// Compartilhar localização (pedido 2026-09-23) — o dono cria/lista/desativa
// links pelo mapa do PWA (location-share-sheet.tsx); o convidado abre
// /ao-vivo/:token sem sessão (routes/ao-vivo.$token.tsx). gpsDriveGet
// funciona sem token em memória (mesmo caso do wizard de contrato, ver
// gpsdrive-contract-signing.service.ts).
import { gpsDriveDelete, gpsDriveGet, gpsDrivePost } from "@/lib/api/gpsdrive-client";

export interface LocationSharePermissions {
  showSpeed: boolean;
  showAddress: boolean;
  showTrail: boolean;
  showVehicleDetails: boolean;
  allowDirections: boolean;
  showSharerName: boolean;
}

export interface LocationShare extends LocationSharePermissions {
  id: string;
  token: string;
  traccarDeviceId: number;
  expiresAt: string;
  revokedAt: string | null;
  createdAt: string;
  guestName: string | null;
  message: string | null;
  viewCount: number;
  lastViewedAt: string | null;
  active: boolean;
}

export interface CreateLocationShareInput extends LocationSharePermissions {
  traccarDeviceId: number;
  durationMinutes: number;
  guestName?: string;
  message?: string;
}

export interface PublicLocationShare {
  status: "active" | "expired" | "revoked";
  startsAt: string;
  expiresAt: string;
  serverTime: string;
  sharerName: string | null;
  guestName: string | null;
  message: string | null;
  permissions: LocationSharePermissions;
  vehicle: {
    name: string | null;
    plate: string | null;
    model: string | null;
    color: string | null;
    photoUrl: string | null;
  } | null;
  position: {
    lat: number;
    lng: number;
    course: number;
    speedKmh: number | null;
    moving: boolean;
    ignition: boolean | null;
    fixTime: string;
  } | null;
  address: string | null;
  trail: [number, number][] | null;
}

// Rota de carro do convidado até o veículo (navegação dentro da tela
// /ao-vivo). Calculada no backend: Google Routes (com trânsito) e OSRM de
// reserva. Instruções já vêm em português.
export interface LocationShareRouteStep {
  instruction: string;
  type: string;
  modifier: string | null;
  name: string | null;
  distanceM: number;
  durationS: number;
  geometryIndex: number;
}

export interface LocationShareRoute {
  distanceM: number;
  durationS: number;
  geometry: [number, number][];
  steps: LocationShareRouteStep[];
  destination: { lat: number; lng: number };
  calculatedAt: string;
  provider: "google" | "osrm";
  // true = tempo já considera o trânsito atual (Google Routes).
  trafficAware: boolean;
}

export const gpsDriveLocationShareService = {
  create: (input: CreateLocationShareInput) => gpsDrivePost<LocationShare>("/api/v1/location-shares", input),
  listMine: (traccarDeviceId?: number) =>
    gpsDriveGet<LocationShare[]>(
      `/api/v1/location-shares${traccarDeviceId ? `?traccarDeviceId=${traccarDeviceId}` : ""}`,
    ),
  revoke: (id: string) => gpsDriveDelete<LocationShare>(`/api/v1/location-shares/${encodeURIComponent(id)}`),
  getPublic: (token: string, opts: { trail?: boolean; view?: boolean } = {}) =>
    gpsDriveGet<PublicLocationShare>(
      `/api/v1/public/location-shares/${encodeURIComponent(token)}?trail=${opts.trail ? 1 : 0}&view=${opts.view ? 1 : 0}`,
    ),
  getPublicRoute: (token: string, from: { lat: number; lng: number }) =>
    gpsDriveGet<LocationShareRoute>(
      `/api/v1/public/location-shares/${encodeURIComponent(token)}/route?fromLat=${from.lat.toFixed(6)}&fromLng=${from.lng.toFixed(6)}`,
    ),
};

// Link que vai pro convidado. No APK (Capacitor) a WebView já roda em
// https://gpsdrive.com.br (server.url), então a origin atual serve; o
// fallback cobre qualquer origin não-http (file://, capacitor://).
export function locationShareUrl(token: string): string {
  const origin =
    typeof window !== "undefined" && /^https?:/.test(window.location.origin)
      ? window.location.origin
      : "https://gpsdrive.com.br";
  return `${origin}/ao-vivo/${token}`;
}

export function formatRemaining(ms: number): string {
  if (ms <= 0) return "encerrado";
  const totalMin = Math.ceil(ms / 60_000);
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return m > 0 ? `${h}h ${m}min` : `${h}h`;
  return `${m}min`;
}
